/**
 * Single write path for materializing a server HubAsCodePayload into a local hub
 * folder. Every consumer of a pulled payload (pull's write branches, the
 * production mirror, push's post-push sync, replicate) MUST go through
 * materializeHubFolder: the wire payload carries attachment REFS that only parse
 * back after rewriteEvalAttachmentsToLocalPaths, so any write path that skips the
 * rewrite persists wire shapes the next push rejects (gh #3410).
 */

import * as path from 'node:path';
import { writeHubFolder, type WriteHubFolderOptions } from './yaml-writer.js';
import { rewriteEvalAttachmentsToLocalPaths, downloadEvalAttachments } from './eval-attachments.js';
import { downloadBinaryFiles } from './resource-files.js';
import type { FileChangeLog } from './fs-safety.js';
import { slugify } from './utils.js';
import type { HubAsCodePayload } from './types.js';

export interface MaterializeResult {
  /** Attachment files newly written under attachments/ (content-identical files are skipped). */
  attachmentsWritten: number;
  /**
   * Every working-tree path WRITTEN this materialize (hub-relative). Aggregates writeHubFolder
   * (hub.yaml, agents, evals, journeys, inline resource files), downloadBinaryResourceFiles
   * (download_url resource files), and downloadEvalAttachments (attachment bytes). Byte-identical
   * files are skipped and NOT listed, so a caller can announce exactly what changed (gh #3410).
   */
  changed: string[];
  /** Every working-tree path REMOVED this materialize (orphan prunes; a dir is reported as "<dir>/"). */
  removed: string[];
}

// Payloads already materialized. The rewrite mutates the payload in place (refs →
// path strings, eval_attachment_files deleted), so a second run over the same
// object is never valid: the rewrite would TypeError on the string entries, and a
// rewrite tolerant of them would return an empty download list — letting
// downloadEvalAttachments orphan-prune every file in attachments/. Fail loud instead.
const materialized = new WeakSet<object>();

export async function materializeHubFolder(
  hubFolder: string,
  payload: HubAsCodePayload,
  options: WriteHubFolderOptions = {},
): Promise<MaterializeResult> {
  if (materialized.has(payload)) {
    throw new Error(
      'materializeHubFolder called twice on the same payload object — CLI bug. ' +
      'The first run rewrote the payload in place; fetch a fresh payload to write again.',
    );
  }
  materialized.add(payload);

  const attachmentDownloads = rewriteEvalAttachmentsToLocalPaths(payload);
  const writeLog = writeHubFolder(hubFolder, payload, options);
  const resourceDownloads = await downloadBinaryResourceFiles(hubFolder, payload);
  const attachments = await downloadEvalAttachments(hubFolder, attachmentDownloads);

  // Aggregate the FULL working-tree delta so a caller can announce every write/prune. Order:
  // config + inline resources (writeHubFolder) → downloaded binary resources → attachment bytes.
  return {
    attachmentsWritten: attachments.written,
    changed: [...writeLog.changed, ...resourceDownloads.changed, ...attachments.changed],
    removed: [...writeLog.removed, ...attachments.removed],
  };
}

/**
 * Download binary resource files that have download_url but no inline content. Returns the
 * hub-relative paths written (byte-identical files are skipped) so the caller can announce them.
 */
export async function downloadBinaryResourceFiles(hubFolder: string, payload: HubAsCodePayload): Promise<{ changed: string[] }> {
  const resources = payload.resources || [];
  const log: FileChangeLog = { changed: [], removed: [] };
  let downloadCount = 0;

  for (const resource of resources) {
    if (!resource.files) continue;
    const resDir = path.join(hubFolder, 'resources', slugify(resource.name));
    downloadCount += await downloadBinaryFiles(resDir, resource.files, hubFolder, log);
  }

  if (downloadCount > 0) {
    console.log(`Downloaded ${downloadCount} binary resource file(s).`);
  }
  // download_url resource files are reserved by writeHubFolder's writeResourceFileTree, so
  // downloadBinaryFiles only ever writes (never orphan-prunes) — no `removed` to surface here.
  return { changed: log.changed };
}
